<template>
    <!-- Spec 2026-10-14 §2 C/D: the /seller/orders recovery banners — tracking
         failures of the seller's rounds (last 14 days) and 112 orders that
         belong to no round. Both stay hidden while the API does not answer
         (older build) or answers 0: never a fake count. -->
    <div v-if="failedCount > 0 || revertRows.length > 0" class="px-4 pt-3" data-testid="seller-tracking-recovery">
        <div
            v-if="failedCount > 0"
            class="seller-recovery-banner seller-recovery-banner--danger"
            role="alert"
            data-testid="seller-failures-banner"
        >
            <i class="fa fa-exclamation-circle"></i>
            <span class="flex-grow-1">{{ $t("tracking_failure.banner", { n: failedCount }) }}</span>
            <base-button size="sm" type="danger" data-testid="seller-failures-open" @click="failuresVisible = true">
                {{ $t("tracking_failure.view") }}
            </base-button>
        </div>
        <div
            v-if="revertRows.length > 0"
            class="seller-recovery-banner seller-recovery-banner--warning"
            role="alert"
            data-testid="seller-stuck-banner"
        >
            <i class="fa fa-exclamation-triangle"></i>
            <span class="flex-grow-1">{{ $t("tracking_failure.stuck_banner", { n: revertRows.length }) }}</span>
            <base-button
                size="sm"
                type="warning"
                :disabled="revertLoading"
                data-testid="seller-stuck-revert"
                @click="revertStuck"
            >
                <i v-if="revertLoading" class="fa fa-spinner fa-spin mr-1"></i>{{ $t("tracking_failure.revert") }}
            </base-button>
        </div>

        <tracking-failures-modal
            :visible.sync="failuresVisible"
            :bo-packing-id="null"
            mode="seller"
            @changed="load"
        ></tracking-failures-modal>
    </div>
</template>

<script>
import swal from "sweetalert2";
import { SellerService } from "@/resource";
import { apiErrorMessage } from "@/util/api-error";
import { packingRoundErrorKey } from "@/utils/sellerPackingRound";
import {
    BANNER_REFETCH_MS,
    REVERT_MAX_ORDERS,
    normalizeFailures,
    normalizeRevertResult,
    normalizeStuck,
    revertErrorKey,
    revertableRows
} from "@/utils/trackingFailure";
import TrackingFailuresModal from "./TrackingFailuresModal.vue";

export default {
    name: "SellerTrackingRecovery",
    components: { TrackingFailuresModal },
    data() {
        return {
            failedCount: 0,
            stuckRows: [],
            failuresVisible: false,
            revertLoading: false,
            timer: null
        };
    },
    computed: {
        // Only rows the revert can still move are counted and sent; the
        // banner (and its button) disappears when none are left.
        revertRows() {
            return revertableRows(this.stuckRows);
        }
    },
    mounted() {
        this.load();
    },
    beforeDestroy() {
        if (this.timer) clearTimeout(this.timer);
    },
    methods: {
        // Spec §3.2 #1 (seller mount, no round) and #4. Each one fails soft.
        async load() {
            const [failures, stuck] = await Promise.all([
                SellerService.trackingFailures({}).catch(() => null),
                SellerService.stuckNoRound({}).catch(() => null)
            ]);
            this.failedCount = failures ? normalizeFailures(failures).rows.length : 0;
            this.stuckRows = stuck ? normalizeStuck(stuck).rows : [];
        },
        // Called by the page after a cut-off: tracking is minted in the
        // background (Seller_cutoff.go), so look again in 20 s.
        scheduleRefetch() {
            if (this.timer) clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.timer = null;
                this.load();
            }, BANNER_REFETCH_MS);
        },
        async revertStuck() {
            const ids = this.revertRows
                .map(r => Number(r && (r.id || r.ID)))
                .filter(n => n > 0)
                .slice(0, REVERT_MAX_ORDERS);
            if (!ids.length) return;
            const confirmed = await swal.fire({
                icon: "warning",
                title: this.$t("tracking_failure.revert"),
                text: this.$t("tracking_failure.revert_confirm", { n: ids.length }),
                showCancelButton: true,
                confirmButtonText: this.$t("confirm"),
                cancelButtonText: this.$t("cancel")
            });
            if (!confirmed || !confirmed.value) return;
            this.revertLoading = true;
            try {
                const res = await SellerService.revertAwaitingShipment({ order_ids: ids });
                const out = normalizeRevertResult(res);
                this.$notify({
                    type: "success",
                    message: this.$t("tracking_failure.revert_done", { n: out.reverted, total: out.requested || ids.length })
                });
                this.$emit("changed");
                this.load();
            } catch (err) {
                const key = revertErrorKey(packingRoundErrorKey(err));
                swal.fire({
                    icon: "error",
                    title: this.$t("tracking_failure.revert"),
                    text: key && this.$te(key) ? this.$t(key) : apiErrorMessage(err, this.$t)
                });
                this.load();
            } finally {
                this.revertLoading = false;
            }
        }
    }
};
</script>

<style scoped>
.seller-recovery-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 6px;
    margin-bottom: 8px;
    font-weight: 600;
}
.seller-recovery-banner--danger {
    background: #fdecea;
    border: 1px solid #f5c2c0;
    color: #a3271f;
}
.seller-recovery-banner--warning {
    background: #fff4e5;
    border: 1px solid #ffd59a;
    color: #7a4a00;
}
</style>
